/**
 * Fetches translations for a specified language, with optional company-specific overrides.
 *
 * @param {string} language - The language code for the translations (e.g., 'en', 'fr').
 * @param {object} [config={}] - Configuration object with company-specific details.
 * @param {string} [config.companyName] - The name of the company.
 * @param {string} [config.companyUuid] - The UUID of the company.
 * @param {string} [config.env] - The environment of the company.
 * @returns {Promise<object>} A promise that resolves to the merged translation object.
 *
 * @example Basic example
 * import { exportTranslations } from './translationExporter';
 *
 * (async () => {
 *   const translations = await exportTranslations('hr');
 *   console.log(translations);
 * })();
 * @example If you need company override
 * import { exportTranslations } from './translationExporter';
 *
 * (async () => {
 *   const translations = await exportTranslations('fr', {
 *     companyName: 'exampleCompany',
 *     companyUuid: '12345',
 *     companyEnv: 'staging'
 *   });
 *   console.log(translations);
 * })();
 *
 * @throws {Error} If fetching the translations fails, or the translations file has no keys defined the function falls back to the default English translations.
 */
async function exportTranslations(
  locale,
  config = { companyName: '', companyUuid: '' },
) {
  const normalizedLang = String(locale || '')
    .trim()
    .replace(/_/g, '-')
    .toLowerCase();
  const [language, languageTag] = normalizedLang.split('-');
  const localeVariants = languageTag
    ? [language, `${language}-${languageTag}`]
    : [language];
  let name =
    config.companyName && config.companyName.replace(/\s/g, '').toLowerCase();
  // staging companies have 'staging' suffix after company name
  const isStaging = config.env === 'staging';
  if (name && isStaging) name += '-staging';

  try {
    // language = 'ke' should return translation keys instead of translations
    if (language === 'ke') return await getTranslationKeys();

    // start with defaults
    let translations = await getDefaultTranslations();

    // merge different variants (e.g., 'pt' and 'pt-br')
    for (const variant of localeVariants) {
      try {
        const lang = await import(
          /* webpackChunkName: "i18n.translation" */ `./dist/${variant}.json`
        );

        const payload = lang && lang.default !== null ? lang.default : lang;
        const langData =
          (payload && payload[variant]) ||
          (payload && payload[language]) ||
          payload;

        if (langData && typeof langData === 'object') {
          translations = { ...translations, ...langData };
        }
      } catch {
        // no requested locale
      }
    }

    if (name && config.companyUuid) {
      const loc = localeVariants[localeVariants.length - 1];
      translations = await mergeCompanyOverride(translations, loc, {
        name,
        companyUuid: config.companyUuid,
      });
    }

    return translations;
  } catch (error) {
    console.warn(
      `Error fetching translations for "${locale}". Returning default (en) language.`,
    );
    return await getDefaultTranslations();
  }
}

async function getTranslationKeys() {
  const defaultTranslations = await getDefaultTranslations();
  const translationKeys = {};

  for (const key in defaultTranslations) {
    translationKeys[key] = key;
  }
  return translationKeys;
}

async function mergeCompanyOverride(translations, locale, config) {
  try {
    const langCompanyOverride = await import(
      /* webpackChunkName: "i18n.translation" */ `./dist/${locale}_${config.name}_${config.companyUuid}.json`
    );
    const companyTranslations =
      langCompanyOverride.default[
        `${locale}_${config.name}_${config.companyUuid}`
      ] || {};
    return { ...translations, ...companyTranslations };
  } catch {
    return translations;
  }
}

async function getDefaultTranslations() {
  const defaultTranslations = await import(
    /* webpackChunkName: "i18n.default.translation" */ './dist/en.json'
  );
  return defaultTranslations.default.en;
}

export { exportTranslations };
